// components/tracking/EventLog.jsx
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip
} from '@mui/material';
import {
  Speed as SpeedIcon,
  Warning as WarningIcon,
  NotListedLocation as GeofenceIcon,
  Battery20 as BatteryIcon,
  DirectionsCar as CarIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';

// Configurar iconos según tipo de evento
const eventIcons = {
  'speed_excess': <SpeedIcon color="error" />,
  'geofence_violation': <GeofenceIcon color="warning" />,
  'battery_low': <BatteryIcon color="warning" />,
  'harsh_acceleration': <SpeedIcon color="warning" />,
  'harsh_breaking': <SpeedIcon color="warning" />,
  // Icono por defecto para eventos no reconocidos
  'default': <WarningIcon color="info" />
};

// Configurar etiquetas según tipo de evento
const eventLabels = {
  'speed_excess': 'Exceso de velocidad',
  'geofence_violation': 'Violación de geocerca',
  'battery_low': 'Batería baja',
  'harsh_acceleration': 'Aceleración brusca',
  'harsh_breaking': 'Frenado brusco'
};

const EventLog = ({ events }) => {
  if (!events || events.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="subtitle1">No hay eventos registrados</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Los eventos de vehículos se mostrarán aquí cuando ocurran
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      <Typography variant="h6" gutterBottom component="div">
        Registro de Eventos
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} component="div">
        Mostrando los últimos {events.length} eventos
      </Typography>
      
      <List>
        {events.map((event, index) => {
          const icon = eventIcons[event.type] || eventIcons.default;
          const label = eventLabels[event.type] || event.type;
          const time = new Date(event.timestamp).toLocaleString();
          
          return (
            <React.Fragment key={index}>
              {index > 0 && <Divider />}
              <ListItem 
                sx={{ 
                  py: 1.5,
                  backgroundColor: index === 0 ? 'rgba(0, 0, 0, 0.03)' : 'transparent'
                }}
              >
                <ListItemIcon>{icon}</ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle2" component="span">{label}</Typography>
                      <Chip 
                        size="small" 
                        label={event.vehicleId}
                        icon={<CarIcon fontSize="small" />}
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box component="div">
                      <Typography variant="body2" color="text.secondary" component="div">
                        {time}
                      </Typography>
                      {event.details && (
                        <Paper variant="outlined" sx={{ mt: 1, p: 1 }}>
                          {event.type === 'speed_excess' && (
                            <Box component="div">
                              <Typography variant="body2" component="div">
                                <strong>Velocidad:</strong> {event.details.speed} km/h
                              </Typography>
                              <Typography variant="body2" component="div">
                                <strong>Límite:</strong> {event.details.limit} km/h
                              </Typography>
                              {event.details.location && (
                                <Typography variant="body2" component="div">
                                  <strong>Ubicación:</strong> {event.details.location}
                                </Typography>
                              )}
                            </Box>
                          )}
                          {/* Aquí puedes agregar más tipos de eventos con sus detalles específicos */}
                        </Paper>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

EventLog.propTypes = {
  events: PropTypes.array.isRequired
};

export default EventLog;